import { PlusIcon } from '@shopify/polaris-icons';
import { Page, Modal, TextField, Select } from '../components/forms';
import { useEffect, useState } from 'react';
import {
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  DataTable,
  FormLayout,
  Checkbox,
  Banner,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';

interface DeliveryRule {
  id: string;
  name: string;
  type: 'global' | 'product' | 'postcode' | 'shipping' | 'cutoff' | 'lead_time';
  priority: number;
  conditions: Record<string, unknown>;
  settings: Record<string, unknown>;
  enabled: number; // 0 or 1 from SQLite
}

export default function DeliveryRules() {
  const { fetchApi, error } = useApi();
  const [rules, setRules] = useState<DeliveryRule[]>([]);
  const [modalActive, setModalActive] = useState(false);
  const [editingRule, setEditingRule] = useState<DeliveryRule | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formType, setFormType] = useState('lead_time');
  const [formPriority, setFormPriority] = useState('10');
  const [formActive, setFormActive] = useState(true);

  // Lead time specific
  const [leadTimeDays, setLeadTimeDays] = useState('1');

  // Cutoff specific
  const [cutoffTime, setCutoffTime] = useState('14:00');
  const [cutoffAddDays, setCutoffAddDays] = useState('1');

  useEffect(() => {
    loadRules();
  }, []);

  async function loadRules() {
    const data = await fetchApi<{ rules: DeliveryRule[] }>('/rules');
    if (data?.rules) {
      setRules(data.rules);
    }
  }

  const openNewRuleModal = () => {
    setEditingRule(null);
    setFormName('');
    setFormType('lead_time');
    setFormPriority('10');
    setFormActive(true);
    setLeadTimeDays('1');
    setCutoffTime('14:00');
    setCutoffAddDays('1');
    setModalActive(true);
  };

  const openEditRuleModal = (rule: DeliveryRule) => {
    setEditingRule(rule);
    setFormName(rule.name);
    setFormType(rule.type);
    setFormPriority(String(rule.priority));
    setFormActive(!!rule.enabled);

    // Parse settings JSON if it's a string
    const settings = typeof rule.settings === 'string'
      ? JSON.parse(rule.settings)
      : rule.settings || {};
    const conditions = typeof rule.conditions === 'string'
      ? JSON.parse(rule.conditions)
      : rule.conditions || {};

    if (rule.type === 'lead_time' || rule.type === 'global') {
      setLeadTimeDays(String(settings.lead_time || 1));
    } else if (rule.type === 'cutoff') {
      setCutoffTime(String(settings.cutoff_time || conditions.cutoff_time || '14:00'));
      setCutoffAddDays(String(settings.add_days || 1));
    }

    setModalActive(true);
  };

  const handleSaveRule = async () => {
    // Build conditions and settings based on rule type
    const conditions: Record<string, unknown> = {};
    let settings: Record<string, unknown> = {};

    if (formType === 'lead_time' || formType === 'global') {
      settings = { lead_time: parseInt(leadTimeDays, 10) };
    } else if (formType === 'cutoff') {
      settings = {
        cutoff_time: cutoffTime,
        add_days: parseInt(cutoffAddDays, 10),
      };
    }

    const ruleData = {
      name: formName,
      type: formType,
      priority: parseInt(formPriority, 10),
      enabled: formActive,
      conditions,
      settings,
    };

    if (editingRule) {
      await fetchApi(`/rules/${editingRule.id}`, {
        method: 'PUT',
        body: JSON.stringify(ruleData),
      });
    } else {
      await fetchApi('/rules', {
        method: 'POST',
        body: JSON.stringify(ruleData),
      });
    }

    setModalActive(false);
    loadRules();
  };

  const handleDeleteRule = async (ruleId: string) => {
    if (confirm('Are you sure you want to delete this rule?')) {
      await fetchApi(`/rules/${ruleId}`, { method: 'DELETE' });
      loadRules();
    }
  };

  const ruleTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      global: 'Global',
      product: 'Product Rule',
      postcode: 'Postcode Rule',
      shipping: 'Shipping Method',
      cutoff: 'Cutoff Time',
      lead_time: 'Lead Time',
    };
    return labels[type] || type;
  };

  const ruleTypeBadge = (type: string) => {
    const tones: Record<string, 'info' | 'success' | 'warning' | 'attention'> = {
      global: 'success',
      product: 'info',
      postcode: 'info',
      shipping: 'success',
      cutoff: 'warning',
      lead_time: 'attention',
    };
    return <Badge tone={tones[type]}>{ruleTypeLabel(type)}</Badge>;
  };

  return (
    <Page
      title="Delivery Rules"
      primaryAction={{
        icon: PlusIcon, content: 'Add Rule',
        onAction: openNewRuleModal,
      }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">Error loading rules: {error}</Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="p" tone="subdued">
                Rules determine which delivery dates are available to customers.
                Rules are evaluated in priority order (lower number = higher priority).
              </Text>

              {rules.length > 0 ? (
                <DataTable
                  columnContentTypes={['text', 'text', 'numeric', 'text', 'text']}
                  headings={['Name', 'Type', 'Priority', 'Status', 'Actions']}
                  rows={rules.map((rule) => [
                    rule.name,
                    ruleTypeBadge(rule.type),
                    rule.priority.toString(),
                    rule.enabled ? (
                      <Badge tone="success">Active</Badge>
                    ) : (
                      <Badge>Inactive</Badge>
                    ),
                    <InlineStack gap="200">
                      <Button size="slim" onClick={() => openEditRuleModal(rule)}>
                        Edit
                      </Button>
                      <Button
                        size="slim"
                        tone="critical"
                        onClick={() => handleDeleteRule(rule.id)}
                      >
                        Delete
                      </Button>
                    </InlineStack>,
                  ])}
                />
              ) : (
                <Text as="p" tone="subdued">
                  No delivery rules configured. Add a rule to get started.
                </Text>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Help Section */}
        <Layout.Section variant="oneThird">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Rule Types</Text>
              <BlockStack gap="200">
                <Text as="p" variant="bodyMd">
                  <strong>Lead Time:</strong> Minimum days before delivery
                </Text>
                <Text as="p" variant="bodyMd">
                  <strong>Cutoff Time:</strong> Order by X time for next day delivery
                </Text>
                <Text as="p" variant="bodyMd">
                  <strong>Product Rule:</strong> Specific lead times for products
                </Text>
                <Text as="p" variant="bodyMd">
                  <strong>Postcode Rule:</strong> Different dates by delivery area
                </Text>
                <Text as="p" variant="bodyMd">
                  <strong>Shipping Method:</strong> Rules for shipping options
                </Text>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Add/Edit Rule Modal */}
      <Modal
        open={modalActive}
        onClose={() => setModalActive(false)}
        title={editingRule ? 'Edit Rule' : 'Add Rule'}
        primaryAction={{
          icon: editingRule ? undefined : PlusIcon, content: 'Save',
          onAction: handleSaveRule,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setModalActive(false),
          },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Rule Name"
              requiredIndicator value={formName}
              onChange={setFormName}
              autoComplete="off"
            />

            <Select
              label="Rule Type"
              options={[
                { label: 'Global (applies to all)', value: 'global' },
                { label: 'Lead Time', value: 'lead_time' },
                { label: 'Cutoff Time', value: 'cutoff' },
                { label: 'Product Rule', value: 'product' },
                { label: 'Postcode Rule', value: 'postcode' },
                { label: 'Shipping Method', value: 'shipping' },
              ]}
              requiredIndicator value={formType}
              onChange={setFormType}
            />

            <TextField
              label="Priority"
              type="number"
              value={formPriority}
              onChange={setFormPriority}
              helpText="Lower number = higher priority"
              autoComplete="off"
            />

            {(formType === 'lead_time' || formType === 'global') && (
              <TextField
                label="Lead Time (days)"
                type="number"
                value={leadTimeDays}
                onChange={setLeadTimeDays}
                helpText="Minimum days before first available delivery date"
                autoComplete="off"
              />
            )}

            {formType === 'cutoff' && (
              <>
                <TextField
                  label="Cutoff Time"
                  type="time"
                  value={cutoffTime}
                  onChange={setCutoffTime}
                  helpText="Orders after this time get pushed to next day"
                  autoComplete="off"
                />
                <TextField
                  label="Add Days"
                  type="number"
                  value={cutoffAddDays}
                  onChange={setCutoffAddDays}
                  helpText="Days to add when cutoff is passed"
                  autoComplete="off"
                />
              </>
            )}

            <Checkbox
              label="Rule is active"
              checked={formActive}
              onChange={setFormActive}
            />
          </FormLayout>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
